import type { AdminGame } from '@mcgames/contracts';
import { Button } from '@mcgames/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useApiMutation } from '../../api/hooks.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { GAMES_PATH, gamePath } from './game-hooks.ts';

/** Router state the games list reads to confirm a deletion. */
export type DeletedGameState = { deletedGame: string };

/** The "Delete game" button of the editor; it asks first and returns to the list afterwards. */
export function DeleteGameControl({ game }: { game: AdminGame }) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const remove = useApiMutation<void>('DELETE', gamePath(game.id), {
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [gamePath(game.id)] });
      void queryClient.invalidateQueries({ queryKey: [GAMES_PATH] });
      const state: DeletedGameState = { deletedGame: game.title };
      void navigate('/admin/games', { state });
    },
  });

  function ask() {
    remove.reset();
    setAsking(true);
  }

  return (
    <>
      <Button variant="danger" onClick={ask}>
        {t('games.delete.button')}
      </Button>
      {asking && (
        <ConfirmDialog
          title={t('games.delete.title')}
          message={t('games.delete.message', { title: game.title })}
          confirmLabel={t('games.delete.confirm')}
          pending={remove.isPending}
          error={remove.error}
          onConfirm={() => remove.mutate()}
          onCancel={() => setAsking(false)}
        />
      )}
    </>
  );
}
